import { gql } from '@apollo/client';
import { mutate } from '../../../../common/graphql';
import { LOCALE } from '../../../../common/i18n';
import { DeveloperPublicProfile } from '@crazygames/game-types/src/developer/developer.type';
import { ROLE_V2 } from '@crazygames/graph-types/roles/roles.types';

export const PROFILE_QUERY = gql`
  query {
    me {
      id
      name
      email
      gameFeedbackEmail {
        email
        isVerified
      }
      iapSupportEmail {
        email
        isVerified
      }
      publicProfile {
        displayName
        tiktok
        discordInvite
        facebook
        instagram
        twitter
        youtube
      }
      locale
      hasAcceptedLegal
      hasOptedOutUserFeedbackEmail
      hasOptedOutProductUpdateEmails
      roles
    }
  }
`;

export const TIPALTI_IFRAME_URLS_QUERY = gql`
  query {
    tipaltiIframeUrls {
      paymentDetailsUrl
      invoiceHistoryUrl
      paymentHistoryUrl
    }
  }
`;

export interface TipaltiUserDataQuery {
  tipaltiUserData: {
    hasCompletedPaymentDetails: boolean;
  };
}

export const TIPALTI_USER_DATA_QUERY = gql`
  query {
    tipaltiUserData {
      hasCompletedPaymentDetails
    }
  }
`;

export interface DeveloperEmail {
  email: string;
  isVerified: boolean;
}

export interface DeveloperProfile {
  id: string;
  name: string;
  email: string;
  publicProfile: DeveloperPublicProfile;
  locale?: LOCALE;
  hasAcceptedLegal: boolean;
  hasOptedOutUserFeedbackEmail: boolean;
  roles: ROLE_V2[];
  gameFeedbackEmail?: DeveloperEmail;
  iapSupportEmail?: DeveloperEmail;
  hasOptedOutProductUpdateEmails?: boolean;
}

export interface ProfileQuery {
  me: DeveloperProfile;
}

export interface DeveloperProfileBillingUrls {
  paymentDetailsUrl: string;
  invoiceHistoryUrl: string;
  paymentHistoryUrl: string;
}

export interface TipaltiIframeUrlsQuery {
  tipaltiIframeUrls: DeveloperProfileBillingUrls;
}

const DEVELOPER_ACCOUNT_GRANULAR_UPDATE_MUTATION = gql`
  mutation saveProfileGranular($input: DeveloperProfileGranularUpdateInput!) {
    developerProfileGranularUpdate(input: $input) {
      id
      name
      email
      hasAcceptedLegal
      hasOptedOutProductUpdateEmails
    }
  }
`;

const PUBLIC_PROFILE_MUTATION = gql`
  mutation savePublicProfile($input: DeveloperPublicProfileUpdateInput!) {
    developerPublicProfileUpdate(input: $input) {
      success
    }
  }
`;

const VALIDATE_CONTACT_EMAIL_MUTATION = gql`
  mutation verifyEmail($input: DeveloperVerifyEmailInput!) {
    developerVerifyEmail(input: $input) {
      id
    }
  }
`;

const ACCOUNT_CREATE = gql`
  mutation createProfile {
    developerCreate {
      id
      name
      email
      hasAcceptedLegal
      hasOptedOutUserFeedbackEmail
    }
  }
`;

export interface AccountCreateMutation {
  developerCreate: DeveloperProfile;
}

export interface PublicProfileMutation {
  developerPublicProfileUpdate: { success: boolean };
}

export interface ValidateContactEmailMutation {
  developerVerifyEmail: {
    id: string;
  };
}

export interface DeveloperProfileGranularUpdateInput {
  name?: string;
  referrer?: string;
  otherReferrer?: string;
  acceptTermsVersion?: string;
  hasOptedOutProductUpdateEmails?: boolean;
}

export interface DeveloperProfileGranularUpdateMutation {
  developerProfileGranularUpdate: DeveloperProfile;
}

export interface DeveloperPublicProfileUpdateInput {
  displayName?: string;
  tiktok?: string;
  discordInvite?: string;
  instagram?: string;
  facebook?: string;
  twitter?: string;
  youtube?: string;
}

export interface ValidateContactEmailInput {
  id: string;
  code: string;
  emailType: string;
}

export function granularSaveAccount(input: DeveloperProfileGranularUpdateInput) {
  return mutate<DeveloperProfileGranularUpdateInput, DeveloperProfileGranularUpdateMutation>(
    DEVELOPER_ACCOUNT_GRANULAR_UPDATE_MUTATION,
    input,
  );
}

export function savePublicProfile(input: DeveloperPublicProfile) {
  return mutate<DeveloperPublicProfile, PublicProfileMutation>(PUBLIC_PROFILE_MUTATION, input, { refetchQueries: [PROFILE_QUERY] });
}

export function validateContactEmail(input: ValidateContactEmailInput) {
  return mutate<ValidateContactEmailInput, ValidateContactEmailMutation>(VALIDATE_CONTACT_EMAIL_MUTATION, input);
}

export function createAccount() {
  return mutate<null, AccountCreateMutation>(ACCOUNT_CREATE, null);
}
